import { Crown, MapPin, GraduationCap, Calendar, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { currentBeautyOfTheWeek } from '@/content/beautyOfTheWeek';
import { Helmet } from 'react-helmet-async';

export default function BeautyOfTheWeekPage() {
  const beauty = currentBeautyOfTheWeek;
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // All photos: main + spread photos
  const allPhotos = [beauty.photoUrl, ...beauty.spreadPhotos];
  
  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % allPhotos.length);
  };
  
  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50">
      <Helmet>
        <title>Beauty of the Week - {beauty.name} | BlackBook</title>
        <meta name="description" content={`Meet ${beauty.name}, ${beauty.age}, from ${beauty.hometown}. ${beauty.yearMajor} at ${beauty.school}. ${beauty.tagline}`} />
        
        <meta property="og:title" content={`Beauty of the Week - ${beauty.name} | BlackBook`} />
        <meta property="og:description" content={`Meet ${beauty.name}, ${beauty.age}, from ${beauty.hometown}. ${beauty.yearMajor} at ${beauty.school}.`} />
        <meta property="og:image" content={`https://www.blackbook.family${beauty.photoUrl}`} />
        <meta property="og:url" content="https://www.blackbook.family/beauty" />
        <meta property="og:type" content="article" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Beauty of the Week - ${beauty.name}`} />
        <meta name="twitter:description" content={`Meet ${beauty.name}, ${beauty.age}, from ${beauty.hometown}.`} />
        <meta name="twitter:image" content={`https://www.blackbook.family${beauty.photoUrl}`} />
      </Helmet>
      {/* Header */}
      <div className="bg-gradient-to-r from-black via-gray-800 to-black text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Crown className="text-yellow-400" size={48} />
            <h1 className="text-4xl md:text-6xl font-bold" data-testid="title-beauty-of-the-week">
              Beauty of the Week
            </h1>
            <Crown className="text-yellow-400" size={48} />
          </div>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Celebrating Black excellence, beauty, and achievement in our community
          </p>
          <div className="flex items-center justify-center gap-2 mt-4 text-yellow-400">
            <Calendar size={20} />
            <span className="font-semibold" data-testid="text-week-range">{beauty.weekRange}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0">
            
            {/* Photo Section with Gallery */}
            <div className="relative bg-gradient-to-br from-purple-100 to-pink-100 p-8 flex items-center justify-center min-h-[600px]">
              <div className="text-center">
                <div className="relative w-80 h-96 rounded-2xl shadow-xl overflow-hidden border-4 border-white">
                  <img 
                    src={allPhotos[currentPhotoIndex]} 
                    alt={`Beauty of the Week - ${beauty.name} - Photo ${currentPhotoIndex + 1}`}
                    className="w-full h-full object-cover transition-all duration-300"
                    data-testid="img-beauty-photo"
                  />
                  
                  {/* Navigation Arrows */}
                  <button 
                    onClick={prevPhoto}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
                    data-testid="button-prev-photo"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button 
                    onClick={nextPhoto}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
                    data-testid="button-next-photo"
                  >
                    <ChevronRight size={20} />
                  </button>
                  
                  {/* Photo Counter */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {currentPhotoIndex + 1} / {allPhotos.length}
                  </div>
                </div>
                
                {/* Photo Thumbnails */}
                <div className="mt-4 flex justify-center gap-2">
                  {allPhotos.map((photo, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPhotoIndex(index)}
                      className={`w-12 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        index === currentPhotoIndex 
                          ? 'border-purple-500 shadow-lg' 
                          : 'border-white/50 hover:border-purple-300'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img 
                        src={photo} 
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
                
                <div className="mt-4 bg-white/90 backdrop-blur-sm rounded-xl p-4 mx-4">
                  <div className="flex items-center justify-center gap-2 text-purple-700">
                    <Crown size={20} />
                    <span className="font-bold">Featured This Week</span>
                    <Crown size={20} />
                  </div>
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <div className="space-y-6">
                
                {/* Name & Age */}
                <div className="text-center lg:text-left">
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-2" data-testid="text-name">
                    {beauty.name}
                  </h2>
                  <p className="text-2xl text-purple-600 font-semibold" data-testid="text-age">
                    {beauty.age} years old
                  </p>
                </div>

                {/* Location */}
                <div className="flex items-center gap-3 text-lg">
                  <MapPin className="text-red-700" size={24} />
                  <span className="text-gray-700" data-testid="text-hometown">{beauty.hometown}</span>
                </div>

                {/* Education */}
                <div className="flex items-center gap-3 text-lg">
                  <GraduationCap className="text-blue-600" size={24} />
                  <div>
                    <p className="text-gray-700 font-semibold" data-testid="text-school">{beauty.school}</p>
                    <p className="text-gray-600" data-testid="text-year-major">{beauty.yearMajor}</p>
                  </div>
                </div>

                {/* Tagline */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border-l-4 border-purple-400">
                  <p className="text-lg text-gray-700 italic leading-relaxed" data-testid="text-tagline">
                    "{beauty.tagline}"
                  </p>
                </div>

                {/* Call to Action */}
                <div className="pt-4">
                  <Button 
                    onClick={() => window.open(beauty.ctaUrl, '_blank')}
                    className="w-full lg:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                    data-testid="button-cta"
                  >
                    <ExternalLink size={20} className="mr-2" />
                    {beauty.ctaText}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="mt-12 bg-gradient-to-r from-black via-gray-800 to-black rounded-3xl p-8 text-center">
          <h3 className="text-3xl font-bold text-yellow-400 mb-6">✨ Celebrating Black Excellence ✨</h3>
          <p className="text-white text-lg leading-relaxed max-w-4xl mx-auto">
            Each week, we spotlight an extraordinary Black woman who embodies excellence, beauty, and achievement. 
            Our Beauty of the Week series celebrates the diverse talents, accomplishments, and inspiring stories 
            within our community. These features highlight students, professionals, entrepreneurs, and leaders who 
            are making a positive impact and serving as role models for the next generation.
          </p>
          <div className="mt-8 grid md:grid-cols-3 gap-6 text-yellow-400">
            <div className="space-y-2">
              <div className="text-2xl font-bold">🏆</div>
              <h4 className="font-semibold">Excellence</h4>
              <p className="text-sm text-gray-300">Academic and professional achievements</p>
            </div>
            <div className="space-y-2">
              <div className="text-2xl font-bold">💫</div>
              <h4 className="font-semibold">Inspiration</h4>
              <p className="text-sm text-gray-300">Stories that motivate and uplift</p>
            </div>
            <div className="space-y-2">
              <div className="text-2xl font-bold">🌟</div>
              <h4 className="font-semibold">Leadership</h4>
              <p className="text-sm text-gray-300">Making positive impacts in communities</p>
            </div>
          </div>
        </div>

        {/* Nomination Info */}
        <div className="mt-8 bg-white rounded-2xl p-8 border-2 border-purple-200">
          <div className="text-center">
            <h4 className="text-2xl font-bold text-gray-800 mb-4">Know Someone Who Should Be Featured?</h4>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              We're always looking for inspiring Black women to feature in our Beauty of the Week series. 
              If you know someone who embodies excellence and achievement, we'd love to hear about them!
            </p>
            <Button 
              onClick={() => window.location.href = 'mailto:info@blackbook.family?subject=Beauty%20of%20the%20Week%20Nomination'}
              className="bg-red-700 hover:bg-red-800 text-white px-8 py-3 rounded-xl font-semibold"
              data-testid="button-nominate"
            >
              Submit a Nomination
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}